import React, { useState } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import HelpNavigationPanel from './components/HelpNavigationPanel';
import TutorialCard from './components/TutorialCard';
import ProgressTracker from './components/ProgressTracker';
import QuickAccessPanel from './components/QuickAccessPanel';
import ContextualHelpLauncher from './components/ContextualHelpLauncher';
import { Search, BookOpen, HelpCircle } from 'lucide-react';

const InAppHelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [showContextualHelp, setShowContextualHelp] = useState(false);

  const handleFilterChange = (filters) => {
    console.log('Filters changed:', filters);
  };

  const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'png-employment-act', label: 'PNG Employment Act Guides' },
  { value: 'nasfund-compliance', label: 'NASFUND Compliance Tutorials' },
  { value: 'swt-processing', label: 'SWT Processing Help' },
  { value: 'department-workflows', label: 'Department-Specific Workflows' },
  { value: 'executive-dashboard', label: 'Executive Dashboard Walkthroughs' },
  { value: 'payroll-compliance', label: 'Payroll Compliance Setup' },
  { value: 'leave-management', label: 'Leave Management Configuration' },
  { value: 'dlir-filing', label: 'DLIR Filing Procedures' },
  { value: 'pacific-rbs', label: 'Pacific RBS Payment Setup' }];


  const departments = [
  { value: 'all', label: 'All Departments' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'finance', label: 'Finance & Payroll' },
  { value: 'compliance', label: 'Compliance & Legal' },
  { value: 'operations', label: 'Operations' },
  { value: 'executive', label: 'Executive Management' }];


  const tutorials = [
  {
    id: 1,
    title: 'PNG Employment Act 1978 Compliance Overview',
    category: 'png-employment-act',
    department: 'compliance',
    duration: '15 min',
    type: 'video',
    difficulty: 'beginner',
    description: 'Comprehensive guide to understanding PNG Employment Act 1978 requirements and how to maintain compliance in your HR operations.',
    completed: true,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1997c8548-1767714695767.png",
    alt: 'Papua New Guinean compliance professional reviewing PNG Employment Act documentation'
  },
  {
    id: 2,
    title: 'NASFUND Contribution Calculation & Filing',
    category: 'nasfund-compliance',
    department: 'finance',
    duration: '20 min',
    type: 'video',
    difficulty: 'intermediate',
    description: 'Step-by-step tutorial on calculating NASFUND contributions, generating reports, and submitting filings to meet government deadlines.',
    completed: true,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1c6da866c-1768531199001.png",
    alt: 'Melanesian finance officer calculating NASFUND contributions for PNG employees'
  },
  {
    id: 3,
    title: 'SWT (Salary & Wages Tax) Processing Guide',
    category: 'swt-processing',
    department: 'finance',
    duration: '18 min',
    type: 'video',
    difficulty: 'intermediate',
    description: 'Complete walkthrough of SWT calculation, deduction processing, and automated filing with PNG Internal Revenue Commission.',
    completed: false,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_11313b954-1768604059029.png",
    alt: 'Pacific Islander tax specialist processing SWT calculations for PNG government filing'
  },
  {
    id: 4,
    title: 'Executive Dashboard Setup & Navigation',
    category: 'executive-dashboard',
    department: 'executive',
    duration: '12 min',
    type: 'video',
    difficulty: 'beginner',
    description: 'Learn how to customize your executive dashboard, interpret compliance scores, and access strategic workforce insights in PNG Kina format.',
    completed: false,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1cec7b6ac-1765212939526.png",
    alt: 'Papua New Guinean executive navigating strategic HR analytics dashboard'
  },
  {
    id: 5,
    title: 'Payroll Compliance Configuration',
    category: 'payroll-compliance',
    department: 'finance',
    duration: '25 min',
    type: 'video',
    difficulty: 'advanced',
    description: 'Advanced setup guide for payroll compliance rules, minimum wage enforcement, overtime calculations, and automated government reporting.',
    completed: false,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_12727438b-1768432087935.png",
    alt: 'Melanesian payroll specialist configuring PNG minimum wage compliance rules'
  },
  {
    id: 6,
    title: 'Leave Management System Configuration',
    category: 'leave-management',
    department: 'hr',
    duration: '16 min',
    type: 'video',
    difficulty: 'intermediate',
    description: 'Configure leave types, balance tracking, approval workflows, and PNG public holiday integration for your organization.',
    completed: false,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_13a8fe736-1770857694486.png",
    alt: 'Pacific Islander HR manager configuring leave management system with PNG holidays'
  },
  {
    id: 7,
    title: 'DLIR Filing Procedures & Deadlines',
    category: 'dlir-filing',
    department: 'compliance',
    duration: '14 min',
    type: 'document',
    difficulty: 'intermediate',
    description: 'Detailed guide to Department of Labour & Industrial Relations filing requirements, submission procedures, and compliance deadlines.',
    completed: false,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1891a8c3e-1768604060126.png",
    alt: 'Papua New Guinean compliance officer reviewing DLIR filing procedures and deadlines'
  },
  {
    id: 8,
    title: 'Pacific RBS Payment Integration Setup',
    category: 'pacific-rbs',
    department: 'finance',
    duration: '22 min',
    type: 'video',
    difficulty: 'advanced',
    description: 'Complete setup guide for Pacific RBS payment gateway integration, including Stripe configuration and PNG Kina payment processing.',
    completed: false,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_174f7ac0b-1768604060071.png",
    alt: 'Melanesian IT specialist setting up Pacific RBS payment gateway for PNG Kina transactions'
  },
  {
    id: 9,
    title: 'Department-Specific Workflow Customization',
    category: 'department-workflows',
    department: 'operations',
    duration: '19 min',
    type: 'video',
    difficulty: 'intermediate',
    description: 'Learn how to customize workflows, approval chains, and reporting structures for different government departments.',
    completed: false,
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_175ac709b-1768445733307.png",
    alt: 'Pacific Islander operations team customizing workflows for PNG government departments'
  }];


  const filteredTutorials = tutorials?.filter((tutorial) => {
    const matchesSearch = searchQuery === '' ||
    tutorial?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    tutorial?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tutorial?.category === selectedCategory;
    const matchesDepartment = selectedDepartment === 'all' || tutorial?.department === selectedDepartment;
    return matchesSearch && matchesCategory && matchesDepartment;
  });

  const completedCount = tutorials?.filter((t) => t?.completed)?.length;
  const totalCount = tutorials?.length;
  const progressPercentage = Math.round(completedCount / totalCount * 100);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <ComplianceAlertBanner />

      <main className="pt-32 pb-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Icon name="HelpCircle" size={32} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                  In-App Help Center
                </h1>
                <p className="text-muted-foreground mt-1">
                  Government-specific tutorials, contextual help, and step-by-step onboarding guides for PNG HR Analytics
                </p>
              </div>
            </div>
          </div>

          {/* Progress Tracker */}
          <ProgressTracker
            completedCount={completedCount}
            totalCount={totalCount}
            progressPercentage={progressPercentage} />


          {/* Search and Filters */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                  <Input
                    type="text"
                    placeholder="Search tutorials, guides, and help topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10" />

                </div>
              </div>
              <Select
                label="Category"
                options={categories}
                value={selectedCategory}
                onChange={setSelectedCategory}
                searchable />

              <Select
                label="Department"
                options={departments}
                value={selectedDepartment}
                onChange={setSelectedDepartment}
                searchable />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  fullWidth
                  iconName="RotateCcw"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                    setSelectedDepartment('all');
                  }}>

                  Reset Filters
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Help Navigation Panel - 3 columns */}
            <div className="lg:col-span-3">
              <HelpNavigationPanel
                categories={categories}
                selectedCategory={selectedCategory}
                onCategorySelect={setSelectedCategory} />

            </div>

            {/* Main Content Area - 9 columns */}
            <div className="lg:col-span-9">
              {/* Tutorial Library */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
                    <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                    Tutorial Library
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {filteredTutorials?.length} {filteredTutorials?.length === 1 ? 'tutorial' : 'tutorials'} found
                  </span>
                </div>

                {filteredTutorials?.length === 0 ?
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No tutorials found</h3>
                    <p className="text-muted-foreground mb-4">
                      Try adjusting your search or filter criteria
                    </p>
                    <Button
                    variant="outline"
                    iconName="RotateCcw"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setSelectedDepartment('all');
                    }}>

                      Reset Filters
                    </Button>
                  </div> :

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredTutorials?.map((tutorial) =>
                  <TutorialCard key={tutorial?.id} tutorial={tutorial} />
                  )}
                  </div>
                }
              </div>

              {/* Quick Access Panel */}
              <QuickAccessPanel />
            </div>
          </div>
        </div>
      </main>

      {/* Contextual Help Launcher */}
      <ContextualHelpLauncher
        isOpen={showContextualHelp}
        onToggle={() => setShowContextualHelp(!showContextualHelp)} />

    </div>);

};

export default InAppHelpCenter;